/**
 * Determines if a new timestamp's time range overlaps with the Off timestamp.
 *
 * Overlap condition: the new timestamp starts at or before the Off time, AND ends strictly after the Off time.
 *
 * @param newStartResolved - Resolved Date for the new timestamp's start
 * @param newEndResolved - Resolved Date for the new timestamp's end
 * @param offDateTime - The Off timestamp's current dateTimeOffset as Date
 * @returns True if the range overlaps Off (start <= off AND end > off)
 */
export function doesOverlapOff(newStartResolved: Date, newEndResolved: Date, offDateTime: Date): boolean {
	return newStartResolved.getTime() <= offDateTime.getTime() && newEndResolved.getTime() > offDateTime.getTime();
}

/**
 * Resolves a time-of-day string (HH:mm) to a full ISO date string, accounting for midnight-crossing shifts.
 *
 * Uses the reference timestamp (typically the last/Off timestamp) as the base date. If the resolved time is after the reference AND the shift has an Off stamp, it subtracts one day (the new time is on the previous day).
 *
 * @param newTimestampTime - Time string in "HH:mm" format
 * @param lastTimestamp - ISO date string of the reference timestamp
 * @param hasOffstamp - Whether the shift has an Off timestamp
 * @returns ISO date string for the resolved time
 */
export function resolveTimeToDate(newTimestampTime: string, lastTimestamp: string, hasOffstamp: boolean = true): string {
	const referenceDate = new Date(lastTimestamp);
	const [newHoursStr, newMinutesStr] = newTimestampTime.split(':');
	const newHours = Number.parseInt(newHoursStr, 10);
	const newMinutes = Number.parseInt(newMinutesStr, 10);
	const newDate = new Date(referenceDate);
	newDate.setHours(newHours, newMinutes, 0, 0);
	if (newDate.getTime() > referenceDate.getTime() && hasOffstamp) {
		newDate.setDate(newDate.getDate() - 1);
	}
	return newDate.toISOString();
}
